// Hero cat keyboard controller + camera follow. WASD or arrow keys move the
// player's cat across the paddy; Shift sprints; the camera lerps to keep the
// cat centered as it moves. Movement is mapped to the camera's isometric view
// so W = up-on-screen even though the world axes are rotated 45° from view.

import * as THREE from 'three'
import gsap from 'gsap'
import { Field } from '../config/balance.js'
import { fieldRadiusWorld, HEX_SIZE, GROUND_Y, isInFieldWorld } from './hexGrid.js'
import { isBlocked } from './collision.js'
import * as advManager from '../game/adventureManager.js'
import { isPlacementActive } from './placement.js'
import { tryShoot as shootWildGoon } from './wildGoons.js'
import { tryShootFriendly } from './peasants.js'
import { goldenStarburst } from './effects.js'
import { groundLift } from './paddyRows.js'
import { getHomeTileIndex, tileToWorld, isOwned } from '../game/plotState.js'
import { GoonConfig, KingdomEmperors } from '../config/balance.js'
import { activeId } from '../game/state.js'
import { Events, on, emit } from '../ui/eventBus.js'
import { leaveAway } from '../game/awayState.js'
import * as toolMode from '../game/toolMode.js'
import { ToolModes } from '../game/toolMode.js'
import * as idle from '../game/idleManager.js' // runtime: gamepad Start → pause
import { getBindings, isCapturing } from '../game/gamepadBindings.js'

const SPEED = 1.25 // world units per second (walking) — halved so the small-scale cats don't look like they're zipping across the field
const SPRINT_MULT = 2 // Shift = sprint, 2× walking speed
// Walk rules override. null = the normal home field (hex bounds + auto-collision).
// While visiting somewhere else (the Capital), capitalScene installs a function
// (x, z, y) => newY | null : given a point and the hero's CURRENT height, the height of the
// floor to stand on there (stairs, bridges, hills…), or null if the hero can't go there.
let walkRules = null
// Current view rotation (radians) — the minimap turns with it so "up" always means up on screen.
export function getViewAzimuth() { return azimuthCurrent }
export function setWalkRules(fn) { walkRules = typeof fn === 'function' ? fn : null; clickTarget = null }
export function hasWalkRules() { return walkRules !== null }
const _groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -GROUND_Y)
const _groundHit = new THREE.Vector3()

// Snap the follow-camera onto the hero instantly (used after a long teleport so the
// view doesn't lerp across the world).
export function snapCameraToHero() {
  if (!catRef || !cameraRef) return
  panOffset.set(0, 0, 0)
  cameraRef.position.set(
    catRef.position.x + CAMERA_OFFSET.x, catRef.position.y + CAMERA_OFFSET.y, catRef.position.z + CAMERA_OFFSET.z,
  )
  cameraRef.lookAt(catRef.position.x, catRef.position.y, catRef.position.z)
}

// Clamp the cat inside the hex field. Square bounding clamp at ~one tile in
// from the field's world half-width — keeps the cat on the board.
const HALF = fieldRadiusWorld() - HEX_SIZE
const CAMERA_OFFSET = new THREE.Vector3(10, 10, 10) // base camera offset at azimuth 0
const CAMERA_LERP_RATE = 5 // bigger = snappier follow

// View rotation — the iso camera snaps between the 4 isometric corners (Q/E or
// the on-screen arrows). azimuthCurrent lerps toward azimuthTarget (index × 90°)
// so the turn arcs smoothly; the SAME angle re-maps the WASD + pan input axes so
// "up" is always up-on-screen regardless of which corner you're viewing from.
const VIEW_TURN_LERP = 8 // bigger = snappier quarter-turn
let azimuthIndex = 0     // unbounded quarter-turn count (sign = direction)
let azimuthTarget = 0    // radians, = azimuthIndex × 90°
let azimuthCurrent = 0   // radians, lerps toward azimuthTarget
const _rotOffset = new THREE.Vector3()

// Rotate (x, z) about the Y axis by `a` — matches THREE Matrix4.makeRotationY so
// the camera offset and the input axes rotate in lockstep.
function rotateY(x, z, a) {
  const c = Math.cos(a)
  const s = Math.sin(a)
  return { x: x * c + z * s, z: -x * s + z * c }
}
// Base camera offset spun to the current view angle (Y height unchanged).
function rotatedOffset() {
  const r = rotateY(CAMERA_OFFSET.x, CAMERA_OFFSET.z, azimuthCurrent)
  return _rotOffset.set(r.x, CAMERA_OFFSET.y, r.z)
}
// Snap the view a quarter-turn. dir < 0 = counter-clockwise, dir > 0 = clockwise.
export function rotateView(dir) {
  azimuthIndex += dir < 0 ? -1 : 1
  azimuthTarget = azimuthIndex * (Math.PI / 2)
}
const CLICK_ARRIVE_RADIUS = 0.15 // close enough to a click target → stop

// Zoom — camera.zoom on the OrthographicCamera. Base frustum is 8 (set in
// sceneCore.js); visible world-height in tile units ≈ (8 / zoom) / 0.816,
// where 0.816 is how much a tile's diamond spans vertically on the isometric
// screen axis.
//   max-in  → home tile + one full ring of neighbors (~3×3 tiles visible)
//   default → medium view, hero tile dominant but ~4–5 tile area in frame
//   max-out → wider survey, but never reveals the whole 12×12 kingdom
const ZOOM_DEFAULT = 2.0 // medium view, a few rings of hexes in frame
const ZOOM_MIN = 0.5  // wide survey — the whole hex kingdom fits on screen
const ZOOM_MAX = 3.5  // close-up — home tile + one ring of neighbors
const ZOOM_STEP = 0.3 // per wheel notch (range is wider now)
const ZOOM_LERP_RATE = 10 // snappy

const keys = { up: false, down: false, left: false, right: false, sprint: false }
let catRef = null
let cameraRef = null
let canvasRef = null
let tilesRef = []

// Camera pan (Shift + drag) — a world-space XZ offset added to the follow
// target so the player can survey the field without moving the cat. Reset by
// the HOME button. Ground-aligned screen axes for the fixed iso camera.
const panOffset = new THREE.Vector3()
let panning = false
let panLastX = 0
let panLastY = 0
let panMoved = false
const _panRight = new THREE.Vector3(0.7071, 0, -0.7071) // screen-right on the ground
const _panFwd = new THREE.Vector3(-0.7071, 0, -0.7071)  // screen-up on the ground

// "Find me" glow — a soft disc at the cat's feet that fades IN while the hero
// moves and OUT when it stops, so the tiny cat is easy to spot in the busy
// scene. Parented to the cat so it follows everywhere (incl. adventures).
let glowMesh = null
let glowOpacity = 0
const _lastCatPos = new THREE.Vector3()

let clickTarget = null // { x, z } in world coords, or null
let targetZoom = ZOOM_DEFAULT
let currentZoom = ZOOM_DEFAULT
// "Gun mode" is now the home-side reading of the global tool mode: the player
// is actively shooting only when DEFENSE is on AND they're not away on an
// adventure (where DEFENSE is a passive stance, not a trigger). Keys 1/2 and
// the reticle are owned by toolMode — see src/game/toolMode.js. Clicks fire
// raycasts against wild goons via wildGoons.tryShoot. Touch requires two taps
// within DOUBLE_TAP_MS to fire (avoids accidental shots while panning).
export function isGunMode() { return toolMode.isDefense() && !advManager.isAdventuring() }
let lastTapAt = 0
const DOUBLE_TAP_MS = 350
const _camTarget = new THREE.Vector3()
const _camLook = new THREE.Vector3()
const _raycaster = new THREE.Raycaster()
const _pointer = new THREE.Vector2()

// Module-level guard: an HMR reload could re-evaluate this file and rebind
// listeners. If `attachHeroController` ever gets called twice we'd otherwise
// register two keydown handlers and stale state would race the live one.
let listenersBound = false

export function attachHeroController(cat, camera, canvas, tiles) {
  catRef = cat
  cameraRef = camera ?? null
  canvasRef = canvas ?? null
  tilesRef = tiles ?? []
  if (import.meta.env?.DEV) { // dev/test hook: where an object (default: the hero) is on screen, in client pixels
    window.__view = { pos: () => ({ x: catRef.position.x, y: catRef.position.y, z: catRef.position.z }), screenOf: (obj = catRef) => { const v = obj.getWorldPosition(new THREE.Vector3()).project(cameraRef); return { x: (v.x * 0.5 + 0.5) * innerWidth, y: (-v.y * 0.5 + 0.5) * innerHeight } } }
  }
  if (listenersBound) {
    resetKeys()
    return
  }
  listenersBound = true
  window.addEventListener('keydown', (e) => handleKey(e, true))
  window.addEventListener('keyup', (e) => handleKey(e, false))
  // Without these resets, a key held while focus leaves the page (alt-tab,
  // modal popup, devtools, etc.) never fires its keyup — so `keys.right` etc.
  // stay `true` forever and the cat keeps drifting. This was the "sometimes
  // WASD doesn't work" sporadic feel.
  window.addEventListener('blur', resetKeys)
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) resetKeys()
  })
  // Re-evaluate the home-shooting HUD lock whenever the tool mode flips (1/2).
  on(Events.ToolModeChange, syncShootingChrome)
  if (canvasRef) {
    canvasRef.addEventListener('click', handleClick)
    // Pointerdown fires before click and carries pointerType — needed to
    // distinguish mouse (single-click shoots) from touch (double-tap shoots).
    canvasRef.addEventListener('pointerdown', handleGunPointer)
    canvasRef.addEventListener('pointerdown', handlePanStart)
    // Move/up on window so a drag keeps panning even outside the canvas.
    window.addEventListener('pointermove', handlePanMove)
    window.addEventListener('pointerup', handlePanEnd)
    // passive:false so we can preventDefault and stop the page from scrolling.
    canvasRef.addEventListener('wheel', handleWheel, { passive: false })
  }
  // Apply default zoom immediately so the world opens at the tight tile view.
  if (cameraRef) {
    cameraRef.zoom = ZOOM_DEFAULT
    cameraRef.updateProjectionMatrix()
  }
  // Bus-triggered teleport — HUD button + future events can fire this.
  on(Events.HackHome, () => hackToHome())
  if (import.meta.env?.DEV) window.__project = (x, z, y = 0.32) => { if (!cameraRef) return null; const v = new THREE.Vector3(x, y, z).project(cameraRef); return { sx: (v.x + 1) / 2 * innerWidth, sy: (1 - v.y) / 2 * innerHeight } } // dev: world → screen px
  if (import.meta.env?.DEV) window.__hero = () => ({ x: catRef.position.x, z: catRef.position.z })
  if (import.meta.env?.DEV) window.__heroTo = (x, z) => { clickTarget = null; resetKeys(); catRef.position.x = x; catRef.position.z = z; snapCameraToHero() } // dev: put the hero somewhere (and the camera on it) — and keep it there: any walk in progress (a click-to-move target, a held key) is cancelled
  if (import.meta.env?.DEV) window.__setZoom = (z) => { targetZoom = z; currentZoom = z; if (cameraRef) { cameraRef.zoom = z; cameraRef.updateProjectionMatrix() } } // dev: wide survey beyond ZOOM_MIN
  // On-screen rotate arrows emit this; Q/E do the same inline (see handleKey).
  on(Events.RotateView, ({ dir } = {}) => rotateView(dir))
}

function handleWheel(e) {
  e.preventDefault()
  // Wheel up (deltaY < 0) zooms IN (larger zoom value).
  const direction = -Math.sign(e.deltaY)
  if (direction === 0) return
  targetZoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, targetZoom + direction * ZOOM_STEP))
}

// Instant teleport to the dead center of the home tile in the current
// kingdom. ALSO snaps the camera + zoom so the player visibly lands "back
// home" rather than watching the camera lerp from wherever they wandered.
// Cancels any pending click-to-move and exits gun mode for a clean reset.
// Where the hero appears at home: on open ground in front of the house, not inside it. Prefers a spot just
// south of the home tile's centre, then searches outward for the nearest unblocked point.
const SPAWN_PREFERRED = { x: 1.1, z: 0.62 }
function heroSpawnPoint(home) {
  const px = home.x + SPAWN_PREFERRED.x, pz = home.z + SPAWN_PREFERRED.z
  if (!isBlocked(px, pz)) return { x: px, z: pz }
  for (let r = 0.2; r <= 1.4; r += 0.2) {
    for (let a = 0; a < 12; a++) {
      const x = px + Math.cos((a / 12) * Math.PI * 2) * r, z = pz + Math.sin((a / 12) * Math.PI * 2) * r
      if (!isBlocked(x, z) && Math.hypot(x - home.x, z - home.z) < HEX_SIZE * 1.3) return { x, z }
    }
  }
  return { x: px, z: pz }
}

export function hackToHome() {
  if (!catRef) return
  // Visiting the Capital: "home" means leaving it (capitalScene does the trip back).
  if (walkRules) { leaveAway(); return }
  const idx = getHomeTileIndex()
  if (idx == null) return
  const home = tileToWorld(idx)
  // Guard against a malformed/legacy home key producing NaN coords — would
  // otherwise send the camera to NaN and blank the whole scene.
  if (!Number.isFinite(home?.x) || !Number.isFinite(home?.z)) {
    console.warn('[hackToHome] bad home coords from key', idx, home)
    return
  }
  const spawn = heroSpawnPoint(home)
  catRef.position.x = spawn.x
  catRef.position.z = spawn.z
  catRef.position.y = GROUND_Y
  clickTarget = null
  panOffset.set(0, 0, 0) // recenter the view on the cat
  azimuthIndex = 0       // snap the view back to the default iso corner
  azimuthTarget = 0
  azimuthCurrent = 0
  resetKeys() // clicking HOME with a key still held would otherwise leave it stuck
  toolMode.setMode(ToolModes.OFF)
  // Spawn-style fade-in — the hero re-materializes at the home tile rather
  // than just teleporting. Wrapper scale tweens from ~0 up to Field.catScale
  // with a soft back.out so it feels like a spirit settling. The inner
  // body/head scales (hunger morph) ride along untouched because they're
  // child-local transforms.
  gsap.killTweensOf(catRef.scale)
  catRef.scale.setScalar(0.01)
  gsap.to(catRef.scale, {
    x: Field.catScale,
    y: Field.catScale,
    z: Field.catScale,
    duration: 0.5,
    ease: 'back.out(2)',
  })
  // Snap the camera instantly to the home view — no lerp wait — and reset
  // zoom to ZOOM_DEFAULT so the player lands at the "fresh boot" framing.
  // tileToWorld only returns { x, z }; the camera's Y comes from the offset.
  if (cameraRef) {
    cameraRef.position.set(
      home.x + CAMERA_OFFSET.x,
      CAMERA_OFFSET.y,
      home.z + CAMERA_OFFSET.z,
    )
    cameraRef.lookAt(home.x, 0, home.z)
    targetZoom = ZOOM_DEFAULT
    currentZoom = ZOOM_DEFAULT
    cameraRef.zoom = ZOOM_DEFAULT
    cameraRef.updateProjectionMatrix()
  }
  // A golden starburst so the player clearly sees where the cat landed.
  goldenStarburst({ scene: catRef.parent, position: new THREE.Vector3(spawn.x, GROUND_Y, spawn.z) })
}

// React to tool-mode changes. The reticle + cursor are owned by toolMode; here
// we only manage the home-shooting side effect: while actively shooting
// (DEFENSE at home), suppress every non-scene click so the player can't
// accidentally open a plot purchase / FEED / BUILD / kingdom-tab dialog
// mid-shoot. The canvas (scene) stays interactive for the shot. In an
// adventure DEFENSE is a passive stance, so the HUD stays clickable (the
// player needs the ADVENTURE button to recall).
function syncShootingChrome() {
  lastTapAt = 0
  const hud = document.getElementById('hud')
  if (hud) hud.style.pointerEvents = isGunMode() ? 'none' : ''
}

// Pointerdown handler — only acts while gun mode is on. For mouse: single
// click shoots. For touch: two taps within DOUBLE_TAP_MS shoot (a single
// stray tap won't fire).
function handleGunPointer(e) {
  if (!isGunMode() || !canvasRef || !cameraRef) return
  if (isPlacementActive()) return
  const rect = canvasRef.getBoundingClientRect()
  _pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1
  _pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1
  if (e.pointerType === 'touch') {
    const now = performance.now()
    if (now - lastTapAt < DOUBLE_TAP_MS) {
      fireShot(e.clientX, e.clientY)
      lastTapAt = 0
    } else {
      lastTapAt = now
    }
    return
  }
  fireShot(e.clientX, e.clientY)
}

// Resolve one shot: wild goon first (the bullet hits the closer threat); if
// the ray didn't land on a goon, check whether the player blasted a loyal
// peasant or their own hero, and emit FriendlyFire so the emperor can scold.
function fireShot(clientX, clientY) {
  toolMode.noteUsed() // a shot counts as using the crosshair: its 10-second timeout starts over
  spawnMuzzleMark(clientX, clientY)
  if (shootWildGoon(cameraRef, _pointer)) return
  const friendly = tryShootFriendly(cameraRef, _pointer)
  if (!friendly) return
  emit(Events.FriendlyFire, friendly)
  // Auto-exit aim mode on a misfire so the emperor's modal close button is
  // clickable (DEFENSE disables HUD pointer-events at home).
  toolMode.setMode(ToolModes.OFF)
  showFriendlyFireBeat(friendly)
}

// Drop a red dot at the click location and fade it out over 500ms — a quick
// "where the bullet hit the screen" tracer so the player gets immediate
// feedback even if they whiffed the shot.
function spawnMuzzleMark(clientX, clientY) {
  if (clientX == null || clientY == null) return
  const dot = document.createElement('div')
  // Solid for 1000ms, then a 500ms fade. Implemented via a transition delay
  // so the browser handles timing — opacity is set to 0 immediately but the
  // animation doesn't start until the delay elapses.
  dot.style.cssText = [
    'position:fixed', `left:${clientX}px`, `top:${clientY}px`,
    'width:2px', 'height:2px', 'margin:-1px 0 0 -1px',
    'background:#ff2a2a',
    'pointer-events:none', 'z-index:9998',
    'opacity:1', 'transition:opacity 500ms linear 1000ms',
  ].join(';')
  document.body.appendChild(dot)
  // Force a layout flush so the initial opacity:1 is committed to the paint
  // tree BEFORE we set opacity:0 — without this the browser collapses both
  // values into one tick and skips the transition (including the delay).
  // Reading offsetWidth is the canonical "flush pending styles" trick.
  void dot.offsetWidth
  dot.style.opacity = '0'
  setTimeout(() => dot.remove(), 1550)
}

function showFriendlyFireBeat(friendly) {
  const emperor = KingdomEmperors[activeId()] ?? KingdomEmperors.WEI
  const body = friendly.type === 'hero'
    ? (emperor.friendlyFireHero?.() ?? '"Caretaker, control yourself."')
    : (emperor.friendlyFireCat?.(GoonConfig[friendly.goonId]?.name ?? 'cat')
       ?? '"A caretaker who kills his own. Pathetic."')
  emit(Events.NarrativeBeat, {
    title: `${emperor.name} ${emperor.cn} — Caretaker's Disgrace`,
    body,
    kingdom: activeId(),
  })
}

// Click anywhere on the paddy → walk the hero there. Disabled during building
// placement (placement.js owns the click in that mode). In gun mode the click
// is consumed by handleGunPointer (which fires earlier on pointerdown), so we
// skip move-to-click here. We also skip if any WASD / arrow key is held so
// the click can't "fight" the keyboard for control.
function handleClick(e) {
  if (!catRef || !cameraRef || !canvasRef) return
  if (isPlacementActive() || isGunMode()) return
  // POSITIVE mode at home is the citizen-care cursor (positiveTool owns the
  // clicks) — suppress click-to-move + plot-claim so the player can tend cats
  // without the hero scampering off. Toggle key 2 off to move again.
  if (toolMode.isPositive() && !advManager.isAdventuring()) return
  // A Shift+drag pan just ended — don't also fire a click-to-move.
  if (e.shiftKey || panMoved) { panMoved = false; return }
  if (keys.up || keys.down || keys.left || keys.right) return
  const rect = canvasRef.getBoundingClientRect()
  _pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1
  _pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1
  _raycaster.setFromCamera(_pointer, cameraRef)
  // In the Capital: click anywhere on the city floor to walk there (walk rules decide
  // whether the hero can actually stand at the destination).
  if (walkRules) {
    _groundPlane.constant = -catRef.position.y // click lands on the plane at the hero's current floor height
    if (_raycaster.ray.intersectPlane(_groundPlane, _groundHit)) {
      clickTarget = { x: _groundHit.x, z: _groundHit.z }
    }
    return
  }
  const hits = _raycaster.intersectObjects(tilesRef, false)
  if (!hits.length) return
  // Wild (unowned) tiles aren't walkable and no longer open the old claim
  // prompt — land is purchased via BUILD → "Buy Land Plot" (claim mode). Just
  // ignore the click. Owned tiles are click-to-move as usual.
  const tileKey = hits[0].object.userData?.tileIndex
  if (tileKey && !isOwned(tileKey)) return
  const p = hits[0].point
  clickTarget = {
    x: Math.max(-HALF, Math.min(HALF, p.x)),
    z: Math.max(-HALF, Math.min(HALF, p.z)),
  }
}

function resetKeys() {
  keys.up = keys.down = keys.left = keys.right = keys.sprint = false
}

// ── Camera pan (Shift + drag) ───────────────────────────────────────────────
function handlePanStart(e) {
  if (!e.shiftKey || e.button !== 0) return
  panning = true
  panMoved = false
  panLastX = e.clientX
  panLastY = e.clientY
}
function handlePanMove(e) {
  if (!panning || !cameraRef || !canvasRef) return
  const dx = e.clientX - panLastX
  const dy = e.clientY - panLastY
  panLastX = e.clientX
  panLastY = e.clientY
  if (dx || dy) panMoved = true
  const rect = canvasRef.getBoundingClientRect()
  // World units per screen pixel at the current zoom (frustum = 8, set in
  // sceneCore). Drag distance → world distance.
  const worldPerPx = (8 / (cameraRef.zoom || 1)) / Math.max(1, rect.height)
  // "Grab the map": dragging right pulls the world right → the camera target
  // moves left, so subtract along screen-right; add along screen-up for dy.
  // Screen axes rotate with the view so the drag stays screen-relative.
  const rRight = rotateY(_panRight.x, _panRight.z, azimuthCurrent)
  const rFwd = rotateY(_panFwd.x, _panFwd.z, azimuthCurrent)
  panOffset.x += (-dx * rRight.x + dy * rFwd.x) * worldPerPx
  panOffset.z += (-dx * rRight.z + dy * rFwd.z) * worldPerPx
  clampPan()
}

// Keep the view target (cat + pan) over the field so the player can't drag the
// camera out into the empty void beyond the tiles/mountains. Clamps the target
// to a disc of ~the field radius (+ a small margin to glimpse the mountain rim).
const PAN_MARGIN = 2
function clampPan() {
  // (Not in the Capital: its walkable world is the whole 226 × 208 m city, far wider than the home field. With this limit the camera
  // stopped following the cat ~86 m from where you arrive, so it walked off-screen — which felt like hitting an invisible wall.)
  if (!catRef || walkRules) return
  const tx = catRef.position.x + panOffset.x
  const tz = catRef.position.z + panOffset.z
  const maxR = fieldRadiusWorld() + PAN_MARGIN
  const d = Math.hypot(tx, tz)
  if (d > maxR) {
    const s = maxR / d
    panOffset.x = tx * s - catRef.position.x
    panOffset.z = tz * s - catRef.position.z
  }
}
function handlePanEnd() {
  panning = false
}

function handleKey(e, pressed) {
  // Don't capture keys when the user is typing in an input/textarea (e.g. onboarding
  // cat-name field, cloud panel save-code field).
  const tag = e.target?.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA') return

  let matched = true
  switch (e.key) {
    case 'ArrowUp':
    case 'w':
    case 'W':
      keys.up = pressed
      break
    case 'ArrowDown':
    case 's':
    case 'S':
      keys.down = pressed
      break
    case 'ArrowLeft':
    case 'a':
    case 'A':
      keys.left = pressed
      break
    case 'ArrowRight':
    case 'd':
    case 'D':
      keys.right = pressed
      break
    case 'Shift':
      keys.sprint = pressed
      break
    case ' ':
      // Space = a cosmetic hop (plays the hero's Jump animation; no gameplay effect).
      // Only on the game page, so Space still scrolls/activates things elsewhere.
      if (pressed && document.body.dataset.activePage === 'play') catRef?.userData.jump?.()
      break
    case 'h':
    case 'H':
      // Only trigger on key DOWN to avoid double-fires on hold.
      if (pressed) hackToHome()
      break
    case 'q':
    case 'Q':
      if (pressed) rotateView(-1) // turn the view counter-clockwise
      break
    case 'e':
    case 'E':
      if (pressed) rotateView(1) // turn the view clockwise
      break
    case '1':
      // Standard everywhere: toggle DEFENSE (red) on/off. Home = aim/shoot,
      // adventure = guard stance. toolMode handles the reticle + mutual
      // exclusion with POSITIVE.
      if (pressed) toolMode.toggle(ToolModes.DEFENSE)
      break
    case '2':
      // Standard everywhere: toggle POSITIVE (yellow) on/off. Home = heal/
      // repair (future), adventure = explore for treasure.
      if (pressed) toolMode.toggle(ToolModes.POSITIVE)
      break
    default:
      matched = false
  }
  // Stop arrow keys from scrolling the page when used as game controls.
  if (matched && e.key.startsWith('Arrow')) e.preventDefault()
}

// ── Gamepad (controller) support — 1A: in-world controls ─────────────────────
// Polled each frame from tickHero. Movement OR's with WASD (left stick + D-pad);
// action buttons are edge-triggered (fire once per press). Standard Gamepad API,
// Xbox-style layout. Mouse/keyboard are unchanged — this is purely additive.
const padKeys = { up: false, down: false, left: false, right: false, sprint: false }
let padPrev = [] // previous pressed-state per button index (rising-edge detection)

function pollGamepad() {
  if (isCapturing()) return // setup wizard owns the pad
  const pads = navigator.getGamepads ? navigator.getGamepads() : []
  let gp = null
  for (const p of pads) { if (p && p.connected) { gp = p; break } }
  if (!gp) {
    padKeys.up = padKeys.down = padKeys.left = padKeys.right = padKeys.sprint = false
    if (padPrev.length) padPrev = []
    return
  }
  const b = getBindings()
  const DEAD = 0.28
  const ax = (gp.axes[b.moveX] ?? 0) * b.moveXInv // normalized so right = +
  const ay = (gp.axes[b.moveY] ?? 0) * b.moveYInv // normalized so down = +
  const btn = (i) => !!gp.buttons[i]?.pressed
  padKeys.up = ay < -DEAD
  padKeys.down = ay > DEAD
  padKeys.left = ax < -DEAD
  padKeys.right = ax > DEAD
  padKeys.sprint = false
  // Edge-triggered actions (once per press). The "tend" button (b.tend) is owned
  // by the gamepad cursor so it can decide "click UI vs. paw".
  const rising = (i) => btn(i) && !padPrev[i]
  if (rising(b.toolCare)) toolMode.toggle(ToolModes.POSITIVE)
  if (rising(b.toolDefense)) toolMode.toggle(ToolModes.DEFENSE)
  if (rising(b.rotateL)) rotateView(-1)
  if (rising(b.rotateR)) rotateView(1)
  if (rising(b.recall)) hackToHome()
  if (rising(b.pause)) idle.togglePause?.()
  padPrev = gp.buttons.map((p) => p.pressed)
}

// Per-frame: apply pressed-key direction to hero position. Camera at
// (10,10,10) looking at origin → screen-up axis is world (-x, -z),
// screen-right is (+x, -z). So WASD maps diagonally in world space.
//
// If no keys are pressed but a click target exists, walk toward it instead.
// Any keypress cancels the click target so the player can interrupt with WASD.
export function tickHero(dt) {
  if (!catRef) return

  // Animation hints for the rigged hero (cat.js reads these): default to idle, and
  // flip to moving only once we know the hero is actually being driven this frame.
  catRef.userData.moving = false
  catRef.userData.sprint = false

  pollGamepad() // controller input OR's with the keyboard below

  // Walk OVER the rice rows (home field only; the Capital has its own floor heights).
  if (!walkRules) catRef.position.y += (GROUND_Y + groundLift(catRef.position.x, catRef.position.z) - catRef.position.y) * Math.min(1, 14 * dt)

  let dx = 0
  let dz = 0
  if (keys.up || padKeys.up) {
    dx -= 1
    dz -= 1
  }
  if (keys.down || padKeys.down) {
    dx += 1
    dz += 1
  }
  if (keys.left || padKeys.left) {
    dx -= 1
    dz += 1
  }
  if (keys.right || padKeys.right) {
    dx += 1
    dz -= 1
  }

  // Re-map the keyboard direction into the current view orientation so W is
  // always "up on screen", whichever iso corner we're rotated to. (Click-to-move
  // below works in world space already, so it must NOT be rotated — hence here,
  // while dx/dz are still the raw keyboard vector.)
  if (dx !== 0 || dz !== 0) {
    const r = rotateY(dx, dz, azimuthCurrent)
    dx = r.x
    dz = r.z
  }

  // Click-to-move: only when keyboard isn't actively driving.
  if (dx === 0 && dz === 0 && clickTarget) {
    const tx = clickTarget.x - catRef.position.x
    const tz = clickTarget.z - catRef.position.z
    const dist = Math.hypot(tx, tz)
    if (dist <= CLICK_ARRIVE_RADIUS) {
      clickTarget = null
      return
    }
    dx = tx / dist
    dz = tz / dist
  } else if (dx !== 0 || dz !== 0) {
    // Keyboard overrides any click target.
    clickTarget = null
  }

  if (dx === 0 && dz === 0) return

  catRef.userData.moving = true
  catRef.userData.sprint = !!(keys.sprint || padKeys.sprint)

  const len = Math.hypot(dx, dz)
  dx /= len
  dz /= len

  const speed = SPEED * (keys.sprint || padKeys.sprint ? SPRINT_MULT : 1)
  const nx = catRef.position.x + dx * speed * dt
  const nz = catRef.position.z + dz * speed * dt
  // A spot is standable if it's on the hex field AND not inside a solid object
  // (building/wall/tree — see collision.js). Try the full step; if it's blocked,
  // try each axis alone so the cat SLIDES along walls/edges rather than sticking.
  // If both axes are blocked (a corner) it just stops.
  // Escape valve: if the cat is already inside a blocked cell (spawned on a
  // building tile, or a tile changed under it), ignore collision so it can walk
  // back out instead of locking up. Collision re-engages once it's clear.
  const stuck = !walkRules && isBlocked(catRef.position.x, catRef.position.z)
  let floorY = catRef.position.y // where the floor is at the spot we end up on (walk rules may raise/lower it)
  const canStand = walkRules
    ? (x, z) => { const y = walkRules(x, z, catRef.position.y); if (y === null) return false; floorY = y; return true }
    : (x, z) => isInFieldWorld(x, z) && (stuck || !isBlocked(x, z))
  if (canStand(nx, nz)) { catRef.position.x = nx; catRef.position.z = nz }
  else if (canStand(nx, catRef.position.z)) { catRef.position.x = nx }
  else if (canStand(catRef.position.x, nz)) { catRef.position.z = nz }
  // Ease onto the new floor height so stairs and slopes glide instead of popping.
  if (walkRules) catRef.position.y += (floorY - catRef.position.y) * Math.min(1, 18 * dt)
  catRef.rotation.y = Math.atan2(dx, dz)
}

// Camera follow — runs every frame regardless of whether the hero is moving,
// so stopping mid-walk doesn't jerk the camera. Uses frame-rate-independent
// lerp: factor = 1 - exp(-rate * dt) gives the same visual smoothness whether
// you're running at 60fps or 30fps.
// Lazily build the glow disc (radial-gradient texture, additive blend) as a
// child of the cat. Sized in cat-local space so it ends up ~1.5 world units.
function ensureGlow() {
  if (glowMesh || !catRef) return
  const cv = document.createElement('canvas')
  cv.width = cv.height = 128
  const ctx = cv.getContext('2d')
  const g = ctx.createRadialGradient(64, 64, 0, 64, 64, 64)
  g.addColorStop(0, 'rgba(255,248,210,0.85)')   // brighter small center core
  g.addColorStop(0.16, 'rgba(255,236,180,0.45)') // taper back to the original ramp
  g.addColorStop(0.4, 'rgba(255,210,110,0.18)')  // mid (unchanged)
  g.addColorStop(1, 'rgba(255,210,110,0)')        // outer glow (unchanged)
  ctx.fillStyle = g
  ctx.fillRect(0, 0, 128, 128)
  const tex = new THREE.CanvasTexture(cv)
  const mat = new THREE.MeshBasicMaterial({
    map: tex, transparent: true, opacity: 0, depthWrite: false,
    blending: THREE.AdditiveBlending,
  })
  const sizeLocal = 1.25 / (Field.catScale || 0.1625)
  glowMesh = new THREE.Mesh(new THREE.PlaneGeometry(sizeLocal, sizeLocal), mat)
  glowMesh.rotation.x = -Math.PI / 2 // lie flat on the ground
  glowMesh.position.y = 0.05          // just above the cat's feet (cat-local)
  glowMesh.renderOrder = 2
  catRef.add(glowMesh)
  _lastCatPos.copy(catRef.position)
}

// Fade the glow toward 1 while the cat moves, 0 when it stops.
function updateGlow(dt) {
  ensureGlow()
  if (!glowMesh) return
  const moved = catRef.position.distanceToSquared(_lastCatPos) > 1e-7
  _lastCatPos.copy(catRef.position)
  const target = moved ? 1 : 0
  glowOpacity += (target - glowOpacity) * (1 - Math.exp(-9 * dt))
  glowMesh.material.opacity = glowOpacity * 0.5 // peak ~50% — subtle, not obnoxious
  const pulse = 1 + 0.06 * Math.sin(performance.now() * 0.008)
  glowMesh.scale.setScalar(pulse)
}

export function tickCamera(dt) {
  if (!cameraRef || !catRef) return
  updateGlow(dt)
  clampPan() // keep the panned view over the field even as the cat walks
  // Ease the view toward its target quarter-turn (no-op when not rotating).
  azimuthCurrent += (azimuthTarget - azimuthCurrent) * (1 - Math.exp(-VIEW_TURN_LERP * dt))
  // Follow the cat, offset by any Shift+drag pan (HOME resets the pan).
  _camTarget.copy(catRef.position).add(panOffset).add(rotatedOffset())
  const factor = 1 - Math.exp(-CAMERA_LERP_RATE * dt)
  cameraRef.position.lerp(_camTarget, factor)
  _camLook.copy(catRef.position).add(panOffset)
  cameraRef.lookAt(_camLook)

  // Lerp the zoom to its target. Skip the projection-matrix update unless the
  // value actually moved this frame — that call isn't free.
  const zoomFactor = 1 - Math.exp(-ZOOM_LERP_RATE * dt)
  const nextZoom = currentZoom + (targetZoom - currentZoom) * zoomFactor
  if (Math.abs(nextZoom - currentZoom) > 0.0005) {
    currentZoom = nextZoom
    cameraRef.zoom = currentZoom
    cameraRef.updateProjectionMatrix()
  }
}

// ── HMR safety ──────────────────────────────────────────────────────────────
// This module owns singleton state (catRef, the keys object) and binds global
// keydown/keyup listeners ONCE via attachHeroController(), called from main.js
// at boot. A Vite hot-swap of this file (or a dependency that propagates here)
// would create a NEW module instance: main.js's render loop would then call
// the new tickHero (catRef=null, fresh keys), while the OLD listeners keep
// mutating the OLD keys object — so WASD/arrows silently stop working until a
// manual refresh. Force a full page reload on any hot update so the listeners
// and the render loop always share one instance.
if (import.meta.hot) {
  import.meta.hot.accept(() => {
    window.location.reload()
  })
}
